import { Injectable, inject } from '@angular/core'
import { Equipment, Location, LocationEnvironment, LocationSector, MaintenanceStatus } from '@generated/sammathnaur.graphql'
import { TreeViewItem, TreeViewItemTypeEnum } from './components/tree-view/tree-view.type'
import { ToLocationTreeViewItemsParams } from './tree-section.types'
import { EquipmentsService } from '../../../../core/api/equipments/equipments.service'
import { LocationsEnvironmentsService } from '../../../../core/api/locations-environments/locations-environments.service'
import { LocationsSectorsService } from '../../../../core/api/locations-sectors/locations-sectors.service'
import { delay } from '../../../../core/others/delay'
import { fetchAllInBatches } from '../../../../core/others/fetch-all-in-batches'
import {
  LocationLinks,
  ToLocationEnvironmentTreeViewItemsParams,
  ToLocationEquipmentTreeViewItemsParams
} from '../../assets-tree.types'
import { AssetsTreeService } from '../../services/assets-tree.service'

@Injectable({ providedIn: 'root' })
export class TreeSectionService {
  public equipmentsService = inject(EquipmentsService)
  public assetsTreeService = inject(AssetsTreeService)
  public locationsSectorsService = inject(LocationsSectorsService)
  public locationsEnvironmentsService = inject(LocationsEnvironmentsService)

  public async load(item: TreeViewItem): Promise<TreeViewItem[]> {
    const [equipments, locationSectors, locationEnvironments] = await Promise.all([
      this.loadEquipments(item.id),
      this.loadLocationSectors(item.id),
      this.loadLocationEnvironments(item.id),
      delay(1000)
    ]).catch(() => [[], [], []])

    return this.toLocationChildrenTreeViewItems({
      location: item.original as Location,
      equipments,
      locationSectors,
      locationEnvironments
    })
  }

  public toLocationTreeViewItems ({ currentLocations, newLocations }: ToLocationTreeViewItemsParams): TreeViewItem[] {
    const formattedNewLocations = newLocations.map((location) => {
      const inProgressMaintenances = (location.maintenances || []).filter((maintenance) => maintenance.status === MaintenanceStatus.InProgress)
      const hasOnlyMaintenanceInProgress = inProgressMaintenances.some((maintenance) => (
        !maintenance.equipments.length &&
        !maintenance.locationEnvironments.length &&
        !maintenance.locationSectors.length
      ))

      return {
        icon: 'place',
        type: TreeViewItemTypeEnum.Location,
        archived: location.archived,
        isParent: true,
        id: location.id,
        name: location.name,
        original: location,
        hasMaintenanceInProgress: Boolean(inProgressMaintenances.length),
        hasOnlyMaintenanceInProgress
      }
    }) as TreeViewItem[]

    return currentLocations.concat(formattedNewLocations)
  }

  private toLocationChildrenTreeViewItems (params: LocationLinks): TreeViewItem[] {
    const { location, equipments } = params
    return [
      ...this.toLocationSectorTreeViewItems(params),
      ...this.toLocationEquipmentTreeViewItems({
        location,
        equipments: equipments.filter((equipment) => !equipment.locationSector && !equipment.locationEnvironment)
      })
    ] as TreeViewItem[]
  }

  private toLocationSectorTreeViewItems (params: LocationLinks): TreeViewItem[] {
    const { location, equipments, locationSectors, locationEnvironments } = params
    return locationSectors.map((locationSector) => {
      const locationSectorEnvironments = this.toLocationEnvironmentTreeViewItems({
        location,
        equipments,
        locationEnvironments: locationEnvironments.filter((locationEnvironment) => locationEnvironment.locationSector && locationEnvironment.locationSector.id === locationSector.id),
      })

      const locationSectorEquipments = this.toLocationEquipmentTreeViewItems({
        location,
        equipments: equipments.filter((equipment) => !equipment.locationEnvironment && equipment.locationSector && equipment.locationSector.id === locationSector.id)
      })

      const hasMaintenanceInProgress = Boolean((location.maintenances || []).filter((maintenance) => (
        maintenance.status === MaintenanceStatus.InProgress &&
        (maintenance.locationSectors || []).some((maintenanceSector) => maintenanceSector.id === locationSector.id)
      )).length)

      const hasOnlyMaintenanceInProgress = hasMaintenanceInProgress &&
        !locationSectorEnvironments.some((environment) => environment.hasMaintenanceInProgress) &&
        !locationSectorEquipments.some((equipment) => equipment.hasMaintenanceInProgress)

      return {
        icon: 'gite',
        id: locationSector.id,
        name: locationSector.name,
        archived: locationSector.archived,
        type: TreeViewItemTypeEnum.LocationSector,
        original: locationSector,
        hasMaintenanceInProgress,
        hasOnlyMaintenanceInProgress,
        children: [...locationSectorEnvironments, ...locationSectorEquipments]
      } as TreeViewItem
    })
  }

  private toLocationEnvironmentTreeViewItems (params: ToLocationEnvironmentTreeViewItemsParams): TreeViewItem[] {
    const { location, locationEnvironments, equipments } = params
    return locationEnvironments.map((locationEnvironment) => {

      const locationEnvironmentEquipments = this.toLocationEquipmentTreeViewItems({
        location,
        equipments: equipments.filter((equipment) => equipment.locationEnvironment && equipment.locationEnvironment.id === locationEnvironment.id)
      })

      const hasMaintenanceInProgress = Boolean((location.maintenances || []).filter((maintenance) => (
        maintenance.status === MaintenanceStatus.InProgress &&
        (maintenance.locationEnvironments || []).some((maintenanceEnvironment) => maintenanceEnvironment.id === locationEnvironment.id)
      )).length)

      const hasOnlyMaintenanceInProgress = hasMaintenanceInProgress &&
        !locationEnvironmentEquipments.some((equipment) => equipment.hasMaintenanceInProgress)

      return {
        icon: 'meeting_room',
        id: locationEnvironment.id,
        name: locationEnvironment.name,
        archived: locationEnvironment.archived,
        type: TreeViewItemTypeEnum.LocationEnvironment,
        original: locationEnvironment,
        hasMaintenanceInProgress,
        hasOnlyMaintenanceInProgress,
        children: locationEnvironmentEquipments
      } as TreeViewItem
    })
  }

  private toLocationEquipmentTreeViewItems (params: ToLocationEquipmentTreeViewItemsParams): TreeViewItem[] {
    const { location, equipments } = params
    return equipments.map((equipment) => {
      const inProgressMaintenances = (location.maintenances || []).filter((maintenance) => (
        maintenance.status === MaintenanceStatus.InProgress &&
        (maintenance.equipments || []).some((maintenanceEquipment) => maintenanceEquipment.id === equipment.id)
      ))

      return {
        icon: 'precision_manufacturing',
        id: equipment.id,
        name: equipment.name,
        imageUrl: equipment.imageUrl,
        archived: equipment.archived,
        type: TreeViewItemTypeEnum.Equipment,
        original: equipment,
        hasMaintenanceInProgress: Boolean(inProgressMaintenances.length),
        hasOnlyMaintenanceInProgress: Boolean(inProgressMaintenances.length)
      } as TreeViewItem
    })
  }

  private async loadEquipments (locationId: string): Promise<Equipment[]> {
    if (!this.assetsTreeService.hasEquipmentViewPermission()) {
      return []
    }

    this.equipmentsService.reset()
    this.equipmentsService.filterForm.get('archived')?.setValue('all')
    this.equipmentsService.filterForm.get('locationId_in')?.setValue([locationId])
    return fetchAllInBatches<Equipment>(this.equipmentsService as any, 'equipments')
  }

  private async loadLocationSectors (locationId: string): Promise<LocationSector[]> {
    this.locationsSectorsService.filterForm.get('archived')?.setValue('all')
    this.locationsSectorsService.filterForm.get('locationId')?.setValue(locationId)
    return fetchAllInBatches<LocationSector>(this.locationsSectorsService as any, 'locationSectors')
  }

  private async loadLocationEnvironments (locationId: string): Promise<LocationEnvironment[]> {
    this.locationsEnvironmentsService.filterForm.get('archived')?.setValue('all')
    this.locationsEnvironmentsService.filterForm.get('locationId')?.setValue(locationId)
    return fetchAllInBatches<LocationEnvironment>(this.locationsEnvironmentsService as any, 'locationEnvironments')
  }
}
